import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import env from 'config/env';
import { useEncryption } from './EncryptionContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatHistory {
  messages: Message[];
  isLoading: boolean;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  encrypted_history: string | null;
}

interface AIPreferences {
  model_preference: 'gpt-4o-mini' | 'gpt-3.5-turbo';
  temperature: number;
  max_context_entries: number;
  include_mood_analysis: boolean;
  include_location_context: boolean;
  include_weather_context: boolean;
}

interface AIContextType {
  chatHistory: ChatHistory;
  conversations: Conversation[];
  currentConversation: Conversation | null;
  preferences: AIPreferences;
  sendMessage: (message: string, conversationId: string) => Promise<{ message: string; messageCount: number }>;
  clearChat: () => void;
  createConversation: (title: string) => Promise<string>;
  loadConversation: (conversationId: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  updatePreferences: (preferences: Partial<AIPreferences>) => Promise<void>;
  searchJournalEntries: (query: string) => Promise<any>;
  setChatHistory: React.Dispatch<React.SetStateAction<ChatHistory>>;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

const defaultPreferences: AIPreferences = {
  model_preference: 'gpt-4o-mini',
  temperature: 0.7,
  max_context_entries: 5,
  include_mood_analysis: true,
  include_location_context: true,
  include_weather_context: true,
};

export function AIProvider({ children }: { children: React.ReactNode }) {
  const [chatHistory, setChatHistory] = useState<ChatHistory>({
    messages: [],
    isLoading: false,
  });
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [preferences, setPreferences] = useState<AIPreferences>(defaultPreferences);
  const toast = useToast();
  const { encrypt, decrypt } = useEncryption();

  // Load conversations and preferences on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [conversationsRes, preferencesRes] = await Promise.all([
          axios.get(`${env.API_URL}/chat/conversations`),
          axios.get(`${env.API_URL}/ai/preferences`),
        ]);

        // Decrypt conversation titles
        const decryptedConversations = await Promise.all(
          conversationsRes.data.map(async (conv: any) => {
            try {
              const title = await decrypt(conv.encrypted_title);
              return { ...conv, title };
            } catch (error) {
              console.error('Error decrypting conversation title:', error);
              return { ...conv, title: 'Error decrypting title' };
            }
          })
        );

        setConversations(decryptedConversations);
        setPreferences(preferencesRes.data);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, [decrypt]);

  const createConversation = useCallback(async (title: string) => {
    try {
      const encrypted_title = await encrypt(title);
      const response = await axios.post(`${env.API_URL}/chat/conversations`, { 
        encrypted_title,
        encrypted_history: null
      });
      const newConversation = {
        id: response.data.id,
        title,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        encrypted_history: null
      };
      setConversations((prev) => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
      setChatHistory({ messages: [], isLoading: false });
      return newConversation.id;
    } catch (error) {
      toast({
        title: 'Error creating conversation',
        description: 'Could not create new conversation',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast, encrypt]);

  const loadConversation = useCallback(async (conversationId: string) => {
    try {
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation) {
        setCurrentConversation(conversation);
        if (conversation.encrypted_history) {
          const decryptedHistory: Message[] = await decrypt(conversation.encrypted_history) as unknown as Message[];
          setChatHistory({
            messages: decryptedHistory,
            isLoading: false,
          });
        } else {
          setChatHistory({ messages: [], isLoading: false });
        }
      }
    } catch (error) {
      console.log(error);
      toast({
        title: 'Error loading conversation',
        description: 'Could not load conversation messages',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [conversations, decrypt, toast]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.delete(`${env.API_URL}/chat/conversations/${conversationId}`);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setChatHistory({ messages: [], isLoading: false });
      }
    } catch (error) {
      toast({
        title: 'Error deleting conversation',
        description: 'Could not delete conversation',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [currentConversation, toast]);

  const updatePreferences = useCallback(async (newPreferences: Partial<AIPreferences>) => {
    try {
      await axios.put(`${env.API_URL}/ai/preferences`, newPreferences);
      setPreferences((prev) => ({ ...prev, ...newPreferences }));
      toast({
        title: 'Preferences updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating preferences',
        description: 'Could not update AI preferences',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const searchJournalEntries = async (query: string) => {
    try {
      // Get embedding for the query
      const embeddingResponse = await axios.post(`${env.API_URL}/ai/embeddings`, { 
        text: query 
      });
      const queryVector = embeddingResponse.data.embedding;

      // Get all entries for the user
      const response = await axios.get(`${env.API_URL}/journal`);
      const entries = response.data;

      // Process and compute similarities locally
      const processedEntries = await Promise.all(
        entries.map(async (entry: any) => {
          const decryptedContent = await decrypt(entry.encrypted_content);
          
          // Get entry vector - either from stored encrypted vector or generate new one
          let entryVector;
          try {
            if (entry.encrypted_vector) {
              entryVector = await decrypt(entry.encrypted_vector);
            }
          } catch (error) {
            console.log('Could not decrypt stored vector, generating new one:', error);
          }

          // If we couldn't get a valid vector, generate a new one
          if (!entryVector) {
            const entryEmbeddingResponse = await axios.post(`${env.API_URL}/ai/embeddings`, {
              text: decryptedContent
            });
            entryVector = entryEmbeddingResponse.data.embedding;
          }

          // Compute cosine similarity locally
          const similarity = computeCosineSimilarity(queryVector, entryVector);
          const decryptedMoodLabel = await decrypt(entry.encrypted_mood_label);
          return {
            id: entry.id,
            content: decryptedContent,
            created_at: entry.created_at,
            mood_label: entry.mood_label,
            decrypted_mood_label: decryptedMoodLabel,
            location_lat: entry.location_lat,
            location_lng: entry.location_lng,
            weather_data: entry.weather_data,
            similarity_score: similarity
          };
        })
      );

      // Sort by similarity score
      return processedEntries.sort((a, b) => b.similarity_score - a.similarity_score);
    } catch (error) {
      console.error('Error searching journal entries:', error);
      throw error;
    }
  };

  // Helper function to compute cosine similarity
  const computeCosineSimilarity = (vec1: number[], vec2: number[]): number => {
    try {
      // Verify vectors are valid
      if (!Array.isArray(vec1) || !Array.isArray(vec2)) {
        console.error('Invalid vectors:', { vec1, vec2 });
        throw new Error('Invalid vectors provided to similarity computation');
      }

      if (vec1.length !== vec2.length) {
        console.error('Vector length mismatch:', { vec1Length: vec1.length, vec2Length: vec2.length });
        throw new Error('Vectors must have the same length');
      }

      // Log vector stats for debugging
      console.log('Vector stats:', {
        vec1Length: vec1.length,
        vec2Length: vec2.length,
        vec1Sample: vec1.slice(0, 3),
        vec2Sample: vec2.slice(0, 3),
      });

      // Compute dot product
      const dotProduct = vec1.reduce((sum, val, i) => sum + val * vec2[i], 0);
      
      // Compute magnitudes
      const mag1 = Math.sqrt(vec1.reduce((sum, val) => sum + val * val, 0));
      const mag2 = Math.sqrt(vec2.reduce((sum, val) => sum + val * val, 0));
      
      // Check for zero magnitudes
      if (mag1 === 0 || mag2 === 0) {
        console.error('Zero magnitude vector detected:', { mag1, mag2 });
        throw new Error('Zero magnitude vector detected');
      }

      // Log similarity computation details
      const similarity = dotProduct / (mag1 * mag2);
      console.log('Similarity computation:', {
        dotProduct,
        mag1,
        mag2,
        similarity
      });

      return similarity;
    } catch (error) {
      console.error('Error computing similarity:', error);
      return 0; // Return 0 similarity on error
    }
  };

  const sendMessage = async (message: string, conversationId: string) => {
    try {
      // Get context for the message
      const contextResponse = await axios.post(`${env.API_URL}/ai/chat/context`, {
        message,
        conversationId
      });

      // Add user message to chat history
      const updatedMessagesWithUser: Message[] = [
        ...chatHistory.messages,
        { role: 'user' as const, content: message, timestamp: new Date().toISOString() }
      ];

      // Save the updated chat history with user message
      try {
        const encrypted_history = await encrypt(JSON.stringify(updatedMessagesWithUser));
        await axios.put(`${env.API_URL}/chat/conversations/${conversationId}`, {
          encrypted_history
        });
        
        // Update the conversations array with the new history
        setConversations(prev => prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, encrypted_history } 
            : conv
        ));
      } catch (error) {
        console.error('Error saving chat history:', error);
        // Continue processing even if save fails
      }

      // Update state with user message
      setChatHistory({
        messages: updatedMessagesWithUser,
        isLoading: true
      });

      // Decrypt the context
      let decryptedContext: any[] = [];

      if (contextResponse.data.contextType === 'semantic') {
        const semanticContext = await searchJournalEntries(message);
        decryptedContext = semanticContext;
        // Only keep the most relevant entries by similarity score
        decryptedContext.splice(preferences.max_context_entries);
      } else {
        decryptedContext = await Promise.all(contextResponse.data.entries.map(async (entry: any) => ({
          ...entry,
          content: await decrypt(entry.encrypted_content),
          decrypted_mood_label: entry.encrypted_mood_label ? await decrypt(entry.encrypted_mood_label) : null,
          tags: entry.tag_names ? 
            await Promise.all(entry.tag_names.split(',').map(async (name: string) => 
              await decrypt(name)
            )) : []
        })));
      }

      // Process the message with context
      const response = await axios.post(`${env.API_URL}/ai/chat/process`, {
        message,
        decryptedContext,
        contextType: contextResponse.data.contextType,
        systemMessage: contextResponse.data.systemMessage,
        conversationId
      });

      // Update chat history with AI response
      const updatedMessages: Message[] = [
        ...updatedMessagesWithUser,
        { role: 'assistant' as const, content: response.data.message, timestamp: new Date().toISOString() }
      ];
      
      // Save the updated chat history to the database
      try {
        const encrypted_history = await encrypt(JSON.stringify(updatedMessages));
        await axios.put(`${env.API_URL}/chat/conversations/${conversationId}`, {
          encrypted_history
        });
        
        // Update the conversations array with the new history
        setConversations(prev => prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, encrypted_history } 
            : conv
        ));
      } catch (error) {
        console.error('Error saving chat history:', error);
        // Don't throw here, we still want to update the UI
      }

      // Update the state
      setChatHistory({
        messages: updatedMessages,
        isLoading: false
      });

      return {
        message: response.data.message,
        messageCount: response.data.messageCount
      };
    } catch (error) {
      console.error('Error in sendMessage:', error);
      setChatHistory((prev: ChatHistory) => ({
        ...prev,
        isLoading: false
      }));
      throw error;
    }
  };

  const clearChat = useCallback(() => {
    setChatHistory({ messages: [], isLoading: false });
    setCurrentConversation(null);
  }, []);

  return (
    <AIContext.Provider
      value={{
        chatHistory,
        conversations,
        currentConversation,
        preferences,
        sendMessage,
        clearChat,
        createConversation,
        loadConversation,
        deleteConversation,
        updatePreferences,
        searchJournalEntries,
        setChatHistory
      }}
    >
      {children}
    </AIContext.Provider>
  );
}

export function useAI() {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
} 